import { useState, useEffect } from "react";
import {
  refereesService,
  type RefereeScope,
  type RefereesByRegionsResponse,
} from "@/api/referees.service";

const DEBOUNCE_MS = 350;
const PREVIEW_ERROR = "Couldn't load the live preview.";

type LegacyPreviewScope = {
  /** Numeric publication id, or null while unknown. */
  publicationId: number | null;
  /** Currently selected region ids. */
  regionIds: number[];
};

type NewAxisPreviewScope = {
  editionId: string | null;
  rankingEntityIds: string[];
};

type UseRefereesPreviewParams = (LegacyPreviewScope | NewAxisPreviewScope) & {
  /** When false (e.g. a mixed selection), the preview is cleared and not fetched. */
  enabled: boolean;
};

function toRefereeScope(params: LegacyPreviewScope | NewAxisPreviewScope): RefereeScope | null {
  if ("editionId" in params) {
    const { editionId, rankingEntityIds } = params;
    return editionId !== null && rankingEntityIds.length > 0
      ? { editionId, rankingEntityIds }
      : null;
  }
  const regionIds = params.regionIds.filter((id): id is number => typeof id === "number");
  return params.publicationId !== null && regionIds.length > 0
    ? { publicationId: params.publicationId, regionIds }
    : null;
}

export type RefereesPreviewState = {
  preview: RefereesByRegionsResponse | null;
  previewLoading: boolean;
  previewError: string | null;
  uniqueEmails: number;
};

/**
 * Debounced live preview of referees matching the current region selection.
 * Display-only: calls submission-api (via staff-bff) with the user's token,
 * requesting `limit: 0` (we only want the aggregate `uniqueEmails`).
 *
 * Extracted verbatim from GenerateMailingGroupsModal so both that modal and the
 * contextual AddGroupModal share identical debounce/cancellation/error behavior.
 */
export function useRefereesPreview({
  enabled,
  ...scopeParams
}: UseRefereesPreviewParams): RefereesPreviewState {
  const [preview, setPreview] = useState<RefereesByRegionsResponse | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const scope = toRefereeScope(scopeParams);
  // Stable key so the debounce effect only re-fires when the actual selection
  // changes (not on unrelated re-renders that hand us a new array reference).
  const scopeKey = JSON.stringify(scope);

  useEffect(() => {
    if (scope === null || !enabled) {
      setPreview(null);
      setPreviewLoading(false);
      setPreviewError(null);
      return;
    }

    let cancelled = false;
    const timeout = setTimeout(() => {
      setPreviewLoading(true);
      setPreviewError(null);
      refereesService
        .getRefereesByRegions({ ...scope, limit: 0 })
        .then(response => {
          if (cancelled) return;
          setPreview(response);
          setPreviewLoading(false);
        })
        .catch(() => {
          if (cancelled) return;
          setPreview(null);
          setPreviewLoading(false);
          setPreviewError(PREVIEW_ERROR);
        });
    }, DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
    // scopeKey stands in for the scope.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, scopeKey]);

  return {
    preview,
    previewLoading,
    previewError,
    uniqueEmails: preview?.uniqueEmails ?? 0,
  };
}
